import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { RotatingLines } from "react-loader-spinner";
import { isMobile } from "react-device-detect";
import { darkTheme } from "../../Assets/styles/darkTheme";
import { useAuthService } from "../../Context/AuthContext";
import { useCallsService } from "../../Context/CallsContext";
import {
  getConferenceFloorTokenApiCall,
} from "../../API/conferences/conferencesService";
import { normalizeConferenceLanguageKey } from "../../Helpers/callHelper/conferenceLanguageKey";
import { setupSignedHls } from "../../Helpers/callHelper/setupSignedHls";
import { DISABLED_TRANSLATION, INCOMING_VOICE_MAX_GAIN } from "../../Constants/constantValues";
import { useTranslate } from "@tolgee/react";
import { useMediaDeviceSelect } from "@livekit/components-react";
import CallChatPanel from "../CallChatComponents/CallChatPanel";
import CallOptionModal from "./CallOptionModal";
import PollPanel from "../Conferences/PollPanel";
import ControlBar from "./ControlBar";
import SpeakingTranslator from "./SpeakingTranslator";
import ConferenceBroadcastMobileLayout from "./ConferenceBroadcastMobileLayout";
import { createLogger } from "../../utils/logger";

// Caption/TTS sync instrumentation. Gated behind localStorage.EMOTII_DEBUG="1".
const syncLog = createLogger("conf-broadcast");

const FALLBACK_CAPTION_TIMEOUT_MS = 15000;

const TTS_LATENCY_EMA_ATTACK = 0.5;
const TTS_LATENCY_EMA_RELEASE = 0.08;
const TTS_LATENCY_MARGIN_MS = 1500;
const RAW_STREAM_MIN_LIVE_SYNC_SECONDS = 5;
const RAW_STREAM_MAX_LIVE_SYNC_SECONDS = 30; // hard cap - covers the worst observed backlog with margin
const RAW_STREAM_MAX_LATENCY_MARGIN_SECONDS = 6; // gap hls.js requires between liveSyncDuration and liveMaxLatencyDuration
const RAW_STREAM_TARGET_UPDATE_INTERVAL_MS = 3000;

const TTS_DIM_FACTOR = 0.25;
const TTS_SPEAKING_RMS_THRESHOLD = 0.02;
const TTS_SILENCE_HOLD_MS = 400;

const ConferenceBroadcastView = ({
  data,
  callAccess,
  resetCall,
  speakersQueue,
  handleRequestSpeakMessage,
  handleSendCancelSpeakMessage,
  speechRecongnitionMessages,
  translationType,
  chatAllowed,
  pollsAllowed,
  callChatConversation,
  onConversationCreated,
  unreadChatCount,
  activePollBadge,
  onChatOpened,
  onPollOpened,
  activeCallRef,
  addObserver,
  removeObserver,
}) => {
  const { t } = useTranslate();
  const { user } = useAuthService();
  const { selectedAudioOutput, setSelectedAudioOutput, setSelectedAudioInput, setSelectedVideoInput } = useCallsService();

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const ttsHlsRef = useRef(null);
  const ttsAudioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const conferenceLanguageKey = normalizeConferenceLanguageKey(user?.call_language);
  const ttsStream =
    callAccess?.streams?.find((s) => s.language_key === conferenceLanguageKey) ??
    callAccess?.streams?.[0];
  const playbackUrl = callAccess?.raw_playback_url ?? callAccess?.mux_playback_url ?? null;
  const isProvisioning = (callAccess?.provisioning ?? false) && !playbackUrl;
  const hostPresent = data?.host_present ?? false;
  const ttsPlaybackUrl = ttsStream?.playback_url ?? null;
  const rawPlaybackId = callAccess?.raw_playback_id ?? null;
  const ttsPlaybackId = ttsStream?.playback_id ?? null;

  const rawTokenRef = useRef(null);
  const ttsTokenRef = useRef(null);

  const [streamLoading, setStreamLoading] = useState(!!playbackUrl);
  const [streamReconnecting, setStreamReconnecting] = useState(false);
  const [isRequestingFloor, setIsRequestingFloor] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [needsSoundGesture, setNeedsSoundGesture] = useState(false);
  const [playerReloadNonce, setPlayerReloadNonce] = useState(0);

  const reloadPlayers = useCallback(() => {
    setStreamReconnecting(false);
    setStreamLoading(!!playbackUrl);
    setPlayerReloadNonce((n) => n + 1);
  }, [playbackUrl]);

  useEffect(() => {
    if (!chatAllowed && activePanel === "chat") {
      setActivePanel(null);
    }
  }, [chatAllowed]);

  const audioInputDevice = useMediaDeviceSelect({ kind: "audioinput", requestPermissions: false });
  const videoInputDevice = useMediaDeviceSelect({ kind: "videoinput", requestPermissions: false });
  const audioOutputDevice = useMediaDeviceSelect({ kind: "audiooutput", requestPermissions: false });

  const [speakStatus, setSpeakStatus] = useState("ended");
  const [emotiiVoice, setEmotiiVoice] = useState(false);
  const [incomingVolume, setIncomingVolume] = useState(100);
  const [emotiiVolume, setEmotiiVolume] = useState(100);
  const [dimEnabled, setDimEnabled] = useState(true);
  const ttsSpeakingRef = useRef(false);
  const micEnabledRef = useRef(false);
  const isLeavingCallRef = useRef(false);
  const noOp = useCallback(() => Promise.resolve(), []);

  const [callStartTime] = useState(() => Date.now());
  const [duration, setDuration] = useState(0);

  const captionSeenAtRef = useRef(new Map());
  const captionRevealAtRef = useRef(new Map());
  const ttsOnsetQueueRef = useRef([]);
  const ttsLatencyEmaMsRef = useRef(null);
  const videoFragRef = useRef(null);
  const [captionTick, setCaptionTick] = useState(0);

  const conferenceId = data?.conference_id;
  const conferenceTitle = data?.conference?.title;
  const translationActive = translationType && translationType !== DISABLED_TRANSLATION;

  const getVideoWallClockMs = () => {
    const frag = videoFragRef.current;
    const video = videoRef.current;
    if (!frag || !video) return null;
    return frag.programDateTime + (video.currentTime - frag.start) * 1000;
  };

  const updateTtsLatencyEma = (observedMs) => {
    const current = ttsLatencyEmaMsRef.current;
    if (current == null) {
      ttsLatencyEmaMsRef.current = observedMs;
      return;
    }
    const rate = observedMs > current ? TTS_LATENCY_EMA_ATTACK : TTS_LATENCY_EMA_RELEASE;
    ttsLatencyEmaMsRef.current = current + (observedMs - current) * rate;
  };

  const currentLiveSyncTargetSeconds = () => {
    const ema = ttsLatencyEmaMsRef.current;
    if (ema == null) return RAW_STREAM_MIN_LIVE_SYNC_SECONDS;
    const raw = (ema + TTS_LATENCY_MARGIN_MS) / 1000;
    return Math.min(RAW_STREAM_MAX_LIVE_SYNC_SECONDS, Math.max(RAW_STREAM_MIN_LIVE_SYNC_SECONDS, raw));
  };

  const displayedMessages = useMemo(() => {
    const list = speechRecongnitionMessages || [];
    if (!playbackUrl) {
      return list;
    }
    const now = Date.now();
    const seen = captionSeenAtRef.current;
    const revealAt = captionRevealAtRef.current;
    const pending = ttsOnsetQueueRef.current;

    for (let i = list.length - 1; i >= 0; i--) {
      const message = list[i];
      const id = message?.id;
      if (id == null || seen.has(id)) continue;
      seen.set(id, now);
      const deviceSentDate = message.device_sent_date
        ? new Date(message.device_sent_date).getTime()
        : null;
      pending.push({ id, deadline: now + FALLBACK_CAPTION_TIMEOUT_MS, deviceSentDate });
      syncLog.debug("[ConferenceBroadcast][CaptionSync] queued caption for TTS onset", { id });
    }

    for (let i = pending.length - 1; i >= 0; i--) {
      const entry = pending[i];
      if (now >= entry.deadline) {
        if (!revealAt.has(entry.id)) {
          revealAt.set(entry.id, now);
          syncLog.debug(
            "[ConferenceBroadcast][CaptionSync] fallback timeout revealed caption (no TTS onset detected)",
            { id: entry.id }
          );
        }
        pending.splice(i, 1);
      }
    }

    return list.filter((message) => {
      if (message?.id == null) {
        return true;
      }
      return revealAt.has(message.id) && now >= revealAt.get(message.id);
    });
  }, [speechRecongnitionMessages, captionTick, playbackUrl]);

  useEffect(() => {
    const tick = () => setDuration(Math.floor((Date.now() - callStartTime) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [callStartTime]);

  const formatDuration = (s) => {
    if (!s || s < 0 || isNaN(s)) return "00:00";
    s = Math.floor(s);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;

    videoFragRef.current = null;
    captionSeenAtRef.current = new Map();
    captionRevealAtRef.current = new Map();
    ttsOnsetQueueRef.current = [];
    ttsLatencyEmaMsRef.current = null;

    const initialSyncSeconds = currentLiveSyncTargetSeconds();

    return setupSignedHls({
      media: video,
      hlsRefSlot: hlsRef,
      tokenRef: rawTokenRef,
      conferenceId,
      playbackId: rawPlaybackId,
      initialUrl: playbackUrl,
      hlsOverrides: {
        liveSyncDuration: initialSyncSeconds,
        liveMaxLatencyDuration: initialSyncSeconds + RAW_STREAM_MAX_LATENCY_MARGIN_SECONDS,
      },
      onManifestParsed: () => {
        setStreamLoading(false);
        setStreamReconnecting(false);
        video.play().catch(() => {});
      },
      onFragChanged: (_event, data) => {
        const frag = data?.frag;
        if (frag?.programDateTime != null) {
          videoFragRef.current = { programDateTime: frag.programDateTime, start: frag.start };
          syncLog.debug("[ConferenceBroadcast][CaptionSync] FRAG_CHANGED with PDT", {
            programDateTime: new Date(frag.programDateTime).toISOString(),
            start: frag.start,
          });
        } else if (videoFragRef.current == null) {
          syncLog.debug("[ConferenceBroadcast][CaptionSync] FRAG_CHANGED without PDT - sync floor disabled, falling back to base delay");
        }
      },
      onFatalNetwork: () => {
        setStreamLoading(true);
        setStreamReconnecting(true);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawPlaybackId, conferenceId, playerReloadNonce]);

  useEffect(() => {
    if (!playbackUrl) return;
    const id = setInterval(() => {
      const hls = hlsRef.current;
      if (!hls) return;
      const targetSeconds = currentLiveSyncTargetSeconds();
      if (hls.config.liveSyncDuration === targetSeconds) return;
      hls.config.liveSyncDuration = targetSeconds;
      hls.config.liveMaxLatencyDuration = targetSeconds + RAW_STREAM_MAX_LATENCY_MARGIN_SECONDS;
      syncLog.debug("[ConferenceBroadcast][AdaptiveDelay] updated raw video live-sync target", {
        targetSeconds,
        emaMs: ttsLatencyEmaMsRef.current,
      });
    }, RAW_STREAM_TARGET_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [playbackUrl]);

  useEffect(() => {
    const audio = ttsAudioRef.current;
    if (!audio) return;
    ttsOnsetQueueRef.current = [];

    if (!ttsPlaybackUrl) {
      if (ttsHlsRef.current) { ttsHlsRef.current.destroy(); ttsHlsRef.current = null; }
      audio.removeAttribute("src");
      return;
    }

    return setupSignedHls({
      media: audio,
      hlsRefSlot: ttsHlsRef,
      tokenRef: ttsTokenRef,
      conferenceId,
      playbackId: ttsPlaybackId,
      initialUrl: ttsPlaybackUrl,
      onManifestParsed: () => { audio.play().catch(() => {}); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsPlaybackId, conferenceId, playerReloadNonce]);

  useEffect(() => {
    const handlePlaybackStart = (event) => {
      const { id, deviceSentDate } = event.detail || {};
      if (!deviceSentDate) return;
      const videoNowMs = getVideoWallClockMs();
      syncLog.debug("[ConferenceBroadcast][TTSSync] TTS playback vs video", {
        id,
        deviceSentDate,
        videoNowMs: videoNowMs != null ? new Date(videoNowMs).toISOString() : null,
        ttsBehindVideoMs: videoNowMs != null ? videoNowMs - new Date(deviceSentDate).getTime() : null,
      });
    };
    window.addEventListener("emotii-playback-start", handlePlaybackStart);
    return () => window.removeEventListener("emotii-playback-start", handlePlaybackStart);
  }, []);

  useEffect(() => {
    if (!playbackUrl) return;
    const id = setInterval(() => {
      setCaptionTick((t) => (t + 1) % 1000000);
    }, 200);
    return () => clearInterval(id);
  }, [playbackUrl]);

  const applyIncomingVolume = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!audioUnlockedRef.current) { video.muted = true; return; }
    if (incomingVolume === 0) { video.volume = 0; video.muted = true; return; }
    const ducked = dimEnabled && ttsSpeakingRef.current;
    video.volume = (incomingVolume / 100) * INCOMING_VOICE_MAX_GAIN * (ducked ? TTS_DIM_FACTOR : 1);
    video.muted = false;
  }, [incomingVolume, dimEnabled]);
  const applyIncomingVolumeRef = useRef(applyIncomingVolume);
  applyIncomingVolumeRef.current = applyIncomingVolume;

  useEffect(() => { applyIncomingVolume(); }, [applyIncomingVolume]);

  useEffect(() => {
    const audio = ttsAudioRef.current;
    if (!audio) return;
    if (!audioUnlockedRef.current) { audio.muted = true; return; }
    audio.volume = emotiiVolume / 100;
    audio.muted = emotiiVolume === 0;
  }, [emotiiVolume, ttsPlaybackUrl]);

  const enableSound = useCallback(() => {
    audioUnlockedRef.current = true;
    setNeedsSoundGesture(false);
    applyIncomingVolumeRef.current?.();
    const audio = ttsAudioRef.current;
    if (audio) {
      audio.muted = emotiiVolume === 0;
      audio.volume = emotiiVolume / 100;
      audio.play().catch(() => {});
    }
    const video = videoRef.current;
    if (video) video.play().catch(() => {});
  }, [emotiiVolume]);

  useEffect(() => {
    if (!playbackUrl || streamLoading || audioUnlockedRef.current) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    video.muted = false;
    Promise.resolve(video.play())
      .then(() => {
        if (cancelled || video.muted) return;
        audioUnlockedRef.current = true;
        setNeedsSoundGesture(false);
        applyIncomingVolumeRef.current?.();
        const audio = ttsAudioRef.current;
        if (audio) {
          audio.muted = emotiiVolume === 0;
          audio.volume = emotiiVolume / 100;
          audio.play().catch(() => {});
        }
      })
      .catch(() => {
        if (cancelled) return;
        video.muted = true;
        video.play().catch(() => {});
        setNeedsSoundGesture(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackUrl, streamLoading]);

  useEffect(() => {
    if (!needsSoundGesture) return;
    const onGesture = () => enableSound();
    window.addEventListener("pointerdown", onGesture, { once: true });
    return () => window.removeEventListener("pointerdown", onGesture);
  }, [needsSoundGesture, enableSound]);

  useEffect(() => {
    const audio = ttsAudioRef.current;
    if (!audio) return;

    let ctx, source, analyser, raf;
    let cancelled = false;
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      ctx = new Ctor();
      ttsAudioCtxRef.current = ctx;
      source = ctx.createMediaElementSource(audio);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyser.connect(ctx.destination);
    } catch (e) {
      return;
    }

    const buffer = new Uint8Array(analyser.fftSize);
    let speaking = false;
    let silenceSince = 0;
    const tick = () => {
      if (cancelled) return;
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        const v = (buffer[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buffer.length);
      const now = performance.now();
      const wasSpeaking = speaking;
      if (rms > TTS_SPEAKING_RMS_THRESHOLD) {
        speaking = true;
        silenceSince = 0;
      } else if (speaking) {
        if (!silenceSince) silenceSince = now;
        if (now - silenceSince > TTS_SILENCE_HOLD_MS) speaking = false;
      }
      if (!wasSpeaking && speaking) {
        const next = ttsOnsetQueueRef.current.shift();
        if (next != null) {
          const revealedAt = Date.now();
          captionRevealAtRef.current.set(next.id, revealedAt);
          let observedLatencyMs = null;
          if (next.deviceSentDate != null) {
            observedLatencyMs = revealedAt - next.deviceSentDate;
            updateTtsLatencyEma(observedLatencyMs);
          }
          syncLog.debug("[ConferenceBroadcast][CaptionSync] TTS onset revealed caption", {
            id: next.id,
            observedLatencyMs,
          });
        }
      }
      if (ttsSpeakingRef.current !== speaking) {
        ttsSpeakingRef.current = speaking;
        applyIncomingVolumeRef.current();
      }
      raf = requestAnimationFrame(tick);
    };

    const GESTURES = ["click", "pointerdown", "touchstart", "keydown"];
    const resume = () => { ctx.resume().catch(() => {}); };
    resume();
    GESTURES.forEach((evt) => window.addEventListener(evt, resume, { passive: true }));
    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      GESTURES.forEach((evt) => window.removeEventListener(evt, resume));
      try { source.disconnect(); analyser.disconnect(); } catch (e) {}
      ctx.close().catch(() => {});
      ttsAudioCtxRef.current = null;
      ttsSpeakingRef.current = false;
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && selectedAudioOutput && video.setSinkId) {
      video.setSinkId(selectedAudioOutput).catch(() => {});
    }
    const audio = ttsAudioRef.current;
    if (audio && selectedAudioOutput && audio.setSinkId) {
      audio.setSinkId(selectedAudioOutput).catch(() => {});
    }
    const ttsCtx = ttsAudioCtxRef.current;
    if (ttsCtx && selectedAudioOutput && typeof ttsCtx.setSinkId === "function") {
      ttsCtx.setSinkId(selectedAudioOutput).catch(() => {});
    }
  }, [selectedAudioOutput]);

  const handleOpenOptionModal = useCallback(() => setIsOptionModalOpen(true), []);
  const handleCloseOptionModal = useCallback(() => setIsOptionModalOpen(false), []);

  const handleIncomingVolumeChange = useCallback((value, dim) => {
    if (typeof value === "number") setIncomingVolume(value);
    if (typeof dim === "boolean") setDimEnabled(dim);
  }, []);

  const handleRequestFloor = useCallback(() => {
    if (!conferenceId || isRequestingFloor) return;
    setIsRequestingFloor(true);
    getConferenceFloorTokenApiCall(
      conferenceId,
      (response) => {
        setIsRequestingFloor(false);
        const access = response?.data?.access;
        if (access?.token && access?.endpoint) {
          handleRequestSpeakMessage({ floor_token: access.token, floor_endpoint: access.endpoint });
        }
      },
      () => setIsRequestingFloor(false)
    );
  }, [conferenceId, isRequestingFloor, handleRequestSpeakMessage]);

  const handleChatBtn = () => {
    if (activePanel === "chat") { setActivePanel(null); }
    else { setActivePanel("chat"); onChatOpened?.(); }
  };

  const handlePollsBtn = () => {
    if (activePanel === "polls") { setActivePanel(null); }
    else { setActivePanel("polls"); onPollOpened?.(); }
  };

  const handleFeedBtn = () => {
    setActivePanel((v) => (v === "feed" ? null : "feed"));
  };

  const handleAudioBtn = () => {
    setActivePanel((v) => (v === "audio" ? null : "audio"));
  };

  useEffect(() => {
    if (!isMobile || activePanel !== null || !translationActive) return;
    setActivePanel("feed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationActive]);

  if (isMobile) {
    return (
      <ConferenceBroadcastMobileLayout
        conferenceTitle={conferenceTitle}
        durationText={formatDuration(duration)}
        videoRef={videoRef}
        ttsAudioRef={ttsAudioRef}
        playbackUrl={playbackUrl}
        streamLoading={streamLoading}
        streamReconnecting={streamReconnecting}
        isProvisioning={isProvisioning}
        needsSoundGesture={needsSoundGesture}
        enableSound={enableSound}
        reloadPlayers={reloadPlayers}
        speakersQueue={speakersQueue}
        isOptionModalOpen={isOptionModalOpen}
        handleOpenOptionModal={handleOpenOptionModal}
        handleCloseOptionModal={handleCloseOptionModal}
        audioInputDevice={audioInputDevice}
        audioOutputDevice={audioOutputDevice}
        videoInputDevice={videoInputDevice}
        setSelectedAudioInput={setSelectedAudioInput}
        setSelectedAudioOutput={setSelectedAudioOutput}
        setSelectedVideoInput={setSelectedVideoInput}
        activePanel={activePanel}
        chatAllowed={chatAllowed}
        pollsAllowed={pollsAllowed}
        translationActive={translationActive}
        unreadChatCount={unreadChatCount}
        activePollBadge={activePollBadge}
        handleChatBtn={handleChatBtn}
        handlePollsBtn={handlePollsBtn}
        handleFeedBtn={handleFeedBtn}
        handleAudioBtn={handleAudioBtn}
        resetCall={resetCall}
        data={data}
        activeCallRef={activeCallRef}
        callChatConversation={callChatConversation}
        onConversationCreated={onConversationCreated}
        onChatOpened={onChatOpened}
        conferenceId={conferenceId}
        addObserver={addObserver}
        removeObserver={removeObserver}
        translationType={translationType}
        speechRecongnitionMessages={displayedMessages}
        speakStatus={speakStatus}
        setSpeakStatus={setSpeakStatus}
        handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
        handleRequestFloor={handleRequestFloor}
        micEnabledRef={micEnabledRef}
        isLeavingCallRef={isLeavingCallRef}
        canRequestSpeak={true}
        setEmotiiVoice={setEmotiiVoice}
        emotiiVoice={emotiiVoice}
        incomingVolume={incomingVolume}
        dimEnabled={dimEnabled}
        emotiiVolume={emotiiVolume}
        handleIncomingVolumeChange={handleIncomingVolumeChange}
        setEmotiiVolume={setEmotiiVolume}
      />
    );
  }

  return (
    <CallViewWrapper>
      {isOptionModalOpen && (
        <CallOptionModal
          onClose={handleCloseOptionModal}
          audioInputDevice={audioInputDevice}
          audioOutputDevice={audioOutputDevice}
          videoInputDevice={videoInputDevice}
          micAllowed={false}
          cameraAllowed={false}
          isAttendee={true}
          setSelectedAudioInput={setSelectedAudioInput}
          setSelectedAudioOutput={setSelectedAudioOutput}
          setSelectedVideoInput={setSelectedVideoInput}
          isConference={true}
        />
      )}
      <MainContainer>
        <LeftSection>

          <HeaderContainer>
            <NameWrapper>
              <Name>{conferenceTitle}</Name>
              <DurationWrapper>
                <PulsingDot />
                <Duration>{formatDuration(duration)}</Duration>
              </DurationWrapper>
            </NameWrapper>

            </HeaderContainer>

          <Content>
            {activePanel === "chat" && chatAllowed && (
              <CallChatPanel
                callInfo={data}
                onClose={() => setActivePanel(null)}
                activeCallRef={activeCallRef}
                existingConversation={callChatConversation}
                onConversationCreated={onConversationCreated}
                onMessagesRead={() => onChatOpened?.()}
              />
            )}

            {activePanel === "polls" && pollsAllowed && (
              <PollPanel
                conferenceId={conferenceId}
                isOrganizer={false}
                isPresenter={false}
                onClose={() => setActivePanel(null)}
                addObserver={addObserver}
                removeObserver={removeObserver}
              />
            )}

            <ConversationCore>
              <StreamArea>
                {(!playbackUrl || streamLoading) && (
                  <WaitingOverlay>
                    <RotatingLines
                      strokeColor={darkTheme.menu.loadingIndicator}
                      strokeWidth="3"
                      animationDuration="0.75"
                      width="48"
                      visible
                    />
                    <WaitingText>
                      {streamReconnecting
                        ? t("conferences.broadcast_buffering") || "Buffering…"
                        : isProvisioning
                        ? t("conferences.broadcast_provisioning") || "Preparing your stream…"
                        : t("conferences.broadcast_starting") || "Broadcast starting…"}
                    </WaitingText>
                  </WaitingOverlay>
                )}

                <StyledVideo
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  style={{ display: playbackUrl && !streamLoading ? "block" : "none" }}
                />

                <audio ref={ttsAudioRef} autoPlay muted playsInline style={{ display: "none" }} />

                {playbackUrl && (
                  <ReloadButton
                    type="button"
                    onClick={reloadPlayers}
                    title={t("conferences.refresh_stream") || "Refresh stream"}
                    aria-label={t("conferences.refresh_stream") || "Refresh stream"}
                  >
                    ↻
                  </ReloadButton>
                )}

                {needsSoundGesture && playbackUrl && !streamLoading && (
                  <SoundPill type="button" onClick={enableSound}>
                    🔊 {t("conferences.tap_for_sound") || "Tap for sound"}
                  </SoundPill>
                )}

                {speakersQueue?.length > 0 && (
                  <QueueBadge>
                    {speakersQueue.map((speaker, idx) => (
                      <QueueItem key={speaker.id ?? idx}>
                        <QueueDot $isYou={speaker.id === user?.id} />
                        {speaker.name || speaker.id}
                        {speaker.id === user?.id && (
                          <YouLabel> ({t("call_modal.you") || "you"})</YouLabel>
                        )}
                      </QueueItem>
                    ))}
                  </QueueBadge>
                )}
              </StreamArea>

              <ControlBarOverlay>
                <ControlBar
                  isAttendee={true}
                  isMeeting={false}
                  isConference={true}
                  chatAllowed={chatAllowed}
                  pollsAllowed={pollsAllowed}
                  isChatOpen={activePanel === "chat"}
                  isPollsOpen={activePanel === "polls"}
                  unreadChatCount={unreadChatCount}
                  activePollBadge={activePollBadge}
                  handleChatBtn={handleChatBtn}
                  handlePollsBtn={handlePollsBtn}
                  handleCloseCallBtn={resetCall}
                  handleMuteBtn={noOp}
                  handleVideoBtn={noOp}
                  handleShareScreenBtn={noOp}
                  hansleParticipantsBtn={noOp}
                  handleOpenOptionModal={handleOpenOptionModal}
                  handleSwitchGrid={noOp}
                  showSettings={true}
                />
              </ControlBarOverlay>
            </ConversationCore>
          </Content>

        </LeftSection>

        {translationActive && (
          <RightSection>
            <SpeakingTranslator
              translationType={translationType}
              micEnabled={false}
              speechRecongnitionMessages={displayedMessages}
              speakStatus={speakStatus}
              setSpeakStatus={setSpeakStatus}
              handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
              handleRequestSpeakMessage={handleRequestFloor}
              speakersQueue={speakersQueue}
              muteMicrophone={noOp}
              unmuteMicrophone={noOp}
              micEnabledRef={micEnabledRef}
              setEmotiiVoice={setEmotiiVoice}
              emotiiVoice={emotiiVoice}
              useRealtimeApi={false}
              realtimeTranslations={[]}
              canRequestSpeak={true}
              requestSpeakPayload={null}
              onIncomingVolumeChange={handleIncomingVolumeChange}
              onEmotiiVolumeChange={setEmotiiVolume}
              emotiiVolume={emotiiVolume}
              activeCallRef={activeCallRef}
              isConference={true}
              isConferenceAttendee={true}
              incomingVolume={incomingVolume}
              dimEnabled={dimEnabled}
              isLeavingCallRef={isLeavingCallRef}
              room={null}
              localParticipant={null}
            />
          </RightSection>
        )}
      </MainContainer>
    </CallViewWrapper>
  );
};

export default ConferenceBroadcastView;

const CallViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.menu.background};
  position: absolute;
  inset: 0;
  z-index: 20;
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: hidden;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 450px;
  min-width: 450px;
  max-width: 450px;
  height: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  gap: 20px;
  padding: 0 20px;
`;

const ConversationCore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const ControlBarOverlay = styled.div`
  width: 100%;
  margin-top: auto;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 10px 20px;
  align-items: center;
  gap: 20px;
  overflow: hidden;
  flex-shrink: 0;
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  margin-left: 20px;
`;

const Name = styled.h3`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
`;

const DurationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Duration = styled.p`
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  opacity: 0.7;
  margin: 0;
  letter-spacing: 0.3px;
`;

const StreamArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const WaitingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const WaitingText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  margin: 0;
  opacity: 0.7;
`;

const ReloadButton = styled.button`
  position: absolute;
  top: 20px;
  left: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(24, 29, 36, 0.85);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(36, 43, 54, 0.95);
  }
`;

const SoundPill = styled.button`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  background: rgba(24, 29, 36, 0.9);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  z-index: 6;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  &:hover {
    background: rgba(36, 43, 54, 0.95);
  }
`;

const QueueBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(24, 29, 36, 0.85);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 180px;
  z-index: 5;
`;

const QueueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #fff;
`;

const QueueDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $isYou }) => ($isYou ? "#7069fd" : "rgba(255,255,255,0.5)")};
`;

const YouLabel = styled.span`
  font-size: 11px;
  color: #7069fd;
`;

